import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Divider, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem } from '@mui/material';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { CalendarSlot } from '../CalendarContext/SlotActionsMenu/CalendarSlot';
import { useAuth } from '../../session/InternalAuthProvider';
import { AccountData } from '../../api';
import { useLanguage } from '../../common/GeneralUtils';
import { getDateFormat, getTimeFormat } from '../../common/getDateAndTimeFormat';
import { CalendarCreateIcon, CalendarCreateIconName } from './CalendarCreateIcon';

type SlotActionsMenuProps = {
  onAddNote: (slot: CalendarSlot) => void;
  onAddShift: (slot: CalendarSlot) => void;
};

/**
 * Opens on a click on an empty calendar slot and lets staff choose what to create there. Each choice opens
 * the dialog that already exists for it, starting on the slot's date and time.
 */
export const SlotActionsMenu = ({ onAddNote, onAddShift }: SlotActionsMenuProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { shortDateFormat } = getDateFormat(account);
  const {
    slotActionsMenuState: { isOpen, slot, anchorPosition, areaBlock },
    closeSlotActionsMenu,
    openEditOrderDrawer,
    openExperienceAvailabilityDrawer,
  } = useCalendar();

  if (!slot) {
    return null;
  }

  const choose = (action: () => void) => () => {
    closeSlotActionsMenu();
    action();
  };

  return (
    <Menu
      open={isOpen}
      onClose={closeSlotActionsMenu}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      // The brand icons' stroke follows the text color; ink, as drawn in the design.
      sx={{ '& .MuiListItemIcon-root': { color: 'text.primary' } }}
      MenuListProps={{ dense: true, 'aria-label': t('calendar.slot_actions.menu_label') }}>
      <ListSubheader sx={{ lineHeight: '32px' }}>
        {moment(slot.date).locale(language).format(`ddd ${shortDateFormat}`)}
        {' · '}
        <bdi dir="ltr">{moment.unix(slot.startSeconds).utc().format(getTimeFormat(account))}</bdi>
      </ListSubheader>
      <MenuItem onClick={choose(() => openEditOrderDrawer({ slot }))}>
        <ListItemIcon>
          <CalendarCreateIcon name={CalendarCreateIconName.NewOrder} />
        </ListItemIcon>
        <ListItemText>{t('calendar.slot_actions.new_order')}</ListItemText>
      </MenuItem>
      <MenuItem onClick={choose(() => onAddNote(slot))}>
        <ListItemIcon>
          <CalendarCreateIcon name={CalendarCreateIconName.Note} />
        </ListItemIcon>
        <ListItemText>{t('calendar.add_note')}</ListItemText>
      </MenuItem>
      <MenuItem onClick={choose(() => openExperienceAvailabilityDrawer({ slot }))}>
        <ListItemIcon>
          <CalendarCreateIcon name={CalendarCreateIconName.Availability} />
        </ListItemIcon>
        <ListItemText>{t('calendar.add_unavailability')}</ListItemText>
      </MenuItem>
      <MenuItem onClick={choose(() => onAddShift(slot))}>
        <ListItemIcon>
          <CalendarCreateIcon name={CalendarCreateIconName.Shift} />
        </ListItemIcon>
        <ListItemText>
          {account.shifts_as_employee_unavailability
            ? t('calendar.add_employee_unavliability')
            : t('calendar.add_shift')}
        </ListItemText>
      </MenuItem>
      {areaBlock && <Divider />}
      {areaBlock && (
        <MenuItem onClick={choose(areaBlock.open)}>
          <ListItemIcon>
            <CalendarCreateIcon name={CalendarCreateIconName.BlockArea} />
          </ListItemIcon>
          <ListItemText>{t('calendar.slot_actions.block_area', { area: areaBlock.areaLabel })}</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );
};
